<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, type Component } from "vue";
import { getMemoizedData } from "@/js/services/sharedDataService";
import CourseIcon from "@/js/svg/CourseIcon.vue";
import LogoIcon from "@/js/svg/LogoIcon.vue";
import OnSiteIcon from "@/js/svg/OnSiteIcon.vue";
import VideoIcon from "@/js/svg/VideoIcon.vue";
import WebinarIcon from "@/js/svg/WebinarIcon.vue";
import SearchIcon from "@/js/svg/SearchIcon.vue";

type Dictionary = {
    title?: string;
    search?: {
        placeholder?: string;
    };
};

type Props = {
    image: string;
    shopUrl: string;
    dictionary: string;
};

type Hit = {
    id: string | number;
    name?: string;
    type?: string;
    format?: string;
    image_url?: string;
    content_provider?: string;
    seller_name?: string;
    provider?: string;
    recommended_level?: string;
    url_key?: string;
    url?: string;
    logo_url?: string;
    shop_title?: string;
};

type SearchResult = {
    total: number;
    hits: Hit[];
};

type Results = {
    categories: SearchResult;
    products: SearchResult;
    sellers: SearchResult;
};

const props = defineProps<Props>();

const parsedDictionary = computed<Dictionary>(() => {
    try {
        return JSON.parse(props.dictionary);
    } catch (error) {
        console.error("Failed to parse dictionary prop:", error);
        return {};
    }
});

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

const searchingText = "Searching...";
const noResultsText = "No results found";
const categoriesText = "Categories";
const productsText = "Products";

const formatLabels: Record<string, string> = {
    webinar: "Webinar",
    "online course": "Online course",
    "onsite course": "On site course",
    "live stream": "Live stream",
};

const formatIcons: Record<string, Component> = {
    webinar: WebinarIcon,
    "online course": CourseIcon,
    "onsite course": OnSiteIcon,
    "live stream": VideoIcon,
};

const categories = ref<any[]>([]);
const search = ref("");
const minQueryLength = ref(MIN_QUERY_LENGTH);
const isOpen = ref(false);
const isLoading = ref(false);
const highlightedIndex = ref(-1);
const debounceTimer = ref<ReturnType<typeof setTimeout> | null>(null);

const results = ref<Results>({
    categories: { total: 0, hits: [] },
    products: { total: 0, hits: [] },
    sellers: { total: 0, hits: [] },
});

const searchInput = ref<HTMLInputElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);

const hasResults = computed(() => {
    return (
        (results.value.categories?.total || 0) > 0 ||
        (results.value.products?.total || 0) > 0 ||
        (results.value.sellers?.total || 0) > 0
    );
});

const allResults = computed(() => {
    const items: (Hit & { resultType: string })[] = [];

    if (results.value.products?.hits?.length) {
        items.push(
            ...results.value.products.hits
                .slice(0, 3)
                .map((i) => ({ ...i, resultType: "products" })),
        );
    }
    if (results.value.categories?.hits?.length) {
        items.push(
            ...results.value.categories.hits
                .slice(0, 3)
                .map((i) => ({ ...i, resultType: "categories" })),
        );
    }
    if (results.value.sellers?.hits?.length) {
        items.push(
            ...results.value.sellers.hits
                .slice(0, 3)
                .map((i) => ({ ...i, resultType: "sellers" })),
        );
    }

    return items;
});

// --- Methods ---
const getCategories = async () => {
    try {
        const data = await getMemoizedData<{ data: any[] }>("/api/categories");
        categories.value = data?.data || [];
    } catch (error) {
        categories.value = [];
        console.error(error);
    }
};

const onSearchInput = () => {
    if (debounceTimer.value) clearTimeout(debounceTimer.value);

    if (search.value.length < minQueryLength.value) {
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
        isOpen.value = false;
        return;
    }

    debounceTimer.value = setTimeout(() => performSearch(), DEBOUNCE_MS);
};

const performSearch = async () => {
    if (search.value.length < minQueryLength.value) return;

    isLoading.value = true;
    isOpen.value = true;
    highlightedIndex.value = -1;

    try {
        const response = await fetch(
            `/api/search?q=${encodeURIComponent(search.value)}&entity=all`,
        );
        if (!response.ok) throw new Error("Search request failed");

        const data = await response.json();
        results.value = {
            categories: data.results?.categories || { total: 0, hits: [] },
            products: data.results?.products || { total: 0, hits: [] },
            sellers: data.results?.sellers || { total: 0, hits: [] },
        };
    } catch (e) {
        results.value = {
            categories: { total: 0, hits: [] },
            products: { total: 0, hits: [] },
            sellers: { total: 0, hits: [] },
        };
        console.error(e);
    } finally {
        isLoading.value = false;
    }
};

const onFocus = () => {
    if (
        search.value.length >= minQueryLength.value &&
        (hasResults.value || isLoading.value)
    ) {
        isOpen.value = true;
    }
};

const onBlur = () => {
    setTimeout(() => {
        isOpen.value = false;
    }, 200);
};

const closeDropdown = () => {
    isOpen.value = false;
    searchInput.value?.blur();
};

const getGlobalIndex = (type: keyof Results, item: Hit) => {
    let index = 0;

    if (results.value.products?.hits?.length) {
        if (type === "products")
            return results.value.products.hits.slice(0, 3).indexOf(item as any);
        index += Math.min(3, results.value.products.hits.length);
    }

    if (results.value.categories?.hits?.length) {
        if (type === "categories")
            return (
                index +
                results.value.categories.hits.slice(0, 3).indexOf(item as any)
            );
        index += Math.min(3, results.value.categories.hits.length);
    }

    if (results.value.sellers?.hits?.length && type === "sellers") {
        return (
            index + results.value.sellers.hits.slice(0, 3).indexOf(item as any)
        );
    }

    return -1;
};

const highlightNext = () => {
    const items = allResults.value;
    if (items.length === 0) return;
    highlightedIndex.value = Math.min(
        highlightedIndex.value + 1,
        items.length - 1,
    );
};

const highlightPrev = () => {
    highlightedIndex.value = Math.max(highlightedIndex.value - 1, -1);
};

const onEnter = () => {
    const items = allResults.value;
    if (highlightedIndex.value >= 0 && items[highlightedIndex.value]) {
        const item = items[highlightedIndex.value];
        if (item.url) window.location.href = item.url;
    } else {
        window.location.href = getProductSearchUrl();
    }
};

const formatLabel = (value: string | undefined) => {
    if (!value) return "Course";
    return formatLabels[value.toLowerCase()] || value;
};

const getFormatIcon = (value: string | undefined) => {
    if (!value) return CourseIcon;
    return formatIcons[value.toLowerCase()] || CourseIcon;
};

const getProductUrl = (product: Hit) => {
    if (product.url_key) return `/${product.url_key}`;
    return `/search?query=${encodeURIComponent(search.value)}`;
};

const getProductSearchUrl = () => {
    return `/search?query=${encodeURIComponent(search.value)}`;
};

const getSellerUrl = (item: Hit) => {
    if (item.url) return `/marketplace/${item.url}`;
    return `/search?query=${encodeURIComponent(search.value)}`;
};

const handleClickOutside = (e: MouseEvent) => {
    const target = e.target as Node;
    if (
        dropdown.value &&
        !dropdown.value.contains(target) &&
        searchInput.value &&
        !searchInput.value.contains(target)
    ) {
        isOpen.value = false;
    }
};

onMounted(() => {
    getCategories();
    document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
    if (debounceTimer.value) clearTimeout(debounceTimer.value);
});
</script>

<template>
    <section class="w-full flex flex-col gap-6 p-4 md:px-6  lg:px-12 lg:py-8 bg-misty-pine-0">
        <h2
            v-if="parsedDictionary.title"
            class="w-full text-center text-32 md:text-40 lg:text-56 font-mulish font-extrabold leading-120 text-custom-black"
        >
            {{ parsedDictionary.title }}
        </h2>

        <div
            class="relative flex items-center justify-center gap-4 w-full"
        >
            <div
                class="flex items-center w-full py-2.5 px-6 border border-background-color rounded-full max-w-[61.525rem] bg-white p-4"
            >
                <search-icon class="text-16 text-custom-blue"/>
                <input
                    ref="searchInput"
                    v-model="search"
                    type="text"
                    class="w-full bg-transparent outline-none py-2 pl-1 font-mulish text-custom-black text-14 font-normal placeholder:font-light placeholder:text-custom-blue"
                    :placeholder="parsedDictionary.search?.placeholder"
                    maxlength="1000"
                    @input="onSearchInput"
                    @focus="onFocus"
                    @blur="onBlur"
                    @keydown.down.prevent="highlightNext"
                    @keydown.up.prevent="highlightPrev"
                    @keydown.enter.prevent="onEnter"
                    @keydown.escape="closeDropdown"
                />
            </div>

            <div
                v-if="isOpen && (hasResults || isLoading)"
                ref="dropdown"
                class="absolute left-1/2 top-full z-50 mt-3 w-full max-w-[min(90vw,42rem)] min-w-[18rem] max-h-[80vh] -translate-x-1/2 overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-[0_10px_40px_-10px_rgba(0,0,0,0.1)]"
            >
                <div v-if="isLoading" class="p-6 text-center text-graphite-5">
                    <span class="icon-spinner icon-spin text-3xl"></span>
                    <p class="mt-2 text-sm font-mulish">{{ searchingText }}</p>
                </div>

                <div
                    v-else-if="!hasResults && search.length >= minQueryLength"
                    class="p-6 text-center text-graphite-5"
                >
                    <p class="mt-2 text-sm font-mulish">{{ noResultsText }}</p>
                </div>

                <div v-else-if="hasResults" class="flex flex-col p-2">
                    <div
                        v-if="results.products?.total > 0"
                        class="overflow-hidden px-4 pb-4 pt-4"
                    >
                        <h3
                            class="mb-4 text-[14px] font-mulish font-semibold text-graphite-6"
                        >
                            {{ productsText }}
                        </h3>
                        <div class="space-y-0 overflow-hidden">
                            <a
                                v-for="item in results.products.hits.slice(
                                    0,
                                    3,
                                )"
                                :key="'prod-' + item.id"
                                :href="getProductUrl(item)"
                                class="group flex items-stretch gap-4 border-b border-graphite-1 py-2 pl-0 pr-0 transition-all last:border-b-0 hover:bg-gray-50"
                                :class="{
                                    'bg-gray-50':
                                        highlightedIndex ===
                                        getGlobalIndex('products', item),
                                }"
                                @mousedown.prevent
                            >
                                <div
                                    class="relative h-[117px] w-[164px] flex-shrink-0 overflow-hidden bg-gray-100"
                                >
                                    <img
                                        v-if="item.image_url"
                                        :src="item.image_url"
                                        :alt="item.name"
                                        loading="lazy"
                                        class="h-[117px] w-[164px] object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                    <div
                                        class="absolute left-2 top-2 flex items-center gap-1 bg-misty-pine-1 px-2 py-1 text-[11px] font-medium text-misty-pine-5"
                                    >
                                        <component
                                            :is="
                                                getFormatIcon(
                                                    item.type || item.format,
                                                )
                                            "
                                            class="w-4 h-4 shrink-0"
                                        />
                                        {{
                                            formatLabel(
                                                item.type || item.format,
                                            ) || "Course"
                                        }}
                                    </div>
                                </div>

                                <div
                                    class="flex min-h-[117px] flex-1 flex-col justify-between py-0.5"
                                >
                                    <div>
                                        <h4
                                            class="text-[16px] !font-mulish font-medium leading-tight text-graphite-9 line-clamp-2"
                                        >
                                            {{ item.name }}
                                        </h4>
                                        <p
                                            class="mt-1 text-[14px] font-mulish text-graphite-6"
                                        >
                                            {{
                                                item.content_provider ||
                                                item.seller_name ||
                                                item.provider ||
                                                "—"
                                            }}
                                        </p>
                                    </div>

                                    <div
                                        v-if="item.recommended_level"
                                        class="flex flex-wrap gap-2"
                                    >
                                        <span
                                            class="rounded-full font-mulish bg-misty-pine-1 px-2.5 py-1 text-[11px] font-medium text-misty-pine-5"
                                        >
                                            {{ item.recommended_level }}
                                        </span>
                                    </div>
                                </div>
                            </a>
                        </div>

                        <a
                            v-if="results.products.total > 3"
                            :href="getProductSearchUrl()"
                            class="mt-4 flex items-center font-mulish gap-1.5 pl-0 pr-2 text-[13px] font-medium text-gray-600 transition-colors hover:text-gray-900"
                            @mousedown.prevent
                        >
                            View all {{ results.products.total }} Products
                            <svg
                                class="h-4 w-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M14 5l7 7m0 0l-7 7m7-7H3"
                                ></path>
                            </svg>
                        </a>
                    </div>

                    <div
                        v-if="
                            results.products?.total > 0 &&
                            (results.categories?.total > 0 ||
                                results.sellers?.total > 0)
                        "
                        class="-mx-2 border-t border-graphite-1"
                    ></div>

                    <div
                        v-if="results.categories?.total > 0"
                        class="px-4 pb-4 pt-4"
                    >
                        <h3
                            class="mb-3 text-[14px] font-mulish font-semibold text-graphite-6"
                        >
                            {{ categoriesText }}
                        </h3>
                        <div class="space-y-0.5">
                            <a
                                v-for="item in results.categories.hits.slice(
                                    0,
                                    3,
                                )"
                                :key="'cat-' + item.id"
                                :href="item.url"
                                class="block rounded-lg font-mulish py-2 pl-0 pr-2 text-[15px] text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900"
                                :class="{
                                    'bg-gray-50 text-gray-900':
                                        highlightedIndex ===
                                        getGlobalIndex('categories', item),
                                }"
                                @mousedown.prevent
                            >
                                {{ item.name }}
                            </a>
                        </div>
                    </div>

                    <div
                        v-if="
                            results.categories?.total > 0 &&
                            results.sellers?.total > 0
                        "
                        class="-mx-2 border-t border-graphite-1"
                    ></div>

                    <div
                        v-if="results.sellers?.total > 0"
                        class="px-4 pb-4 pt-4"
                    >
                        <h3
                            class="mb-3 text-[14px] font-semibold font-mulish text-graphite-6"
                        >
                            Content Providers
                        </h3>
                        <div class="flex flex-col space-y-1">
                            <a
                                v-for="item in results.sellers.hits.slice(0, 3)"
                                :key="'seller-' + item.id"
                                :href="getSellerUrl(item)"
                                class="flex items-center gap-3 font-mulish rounded-lg py-2 pl-0 pr-2 transition-colors hover:bg-gray-50"
                                :class="{
                                    'bg-gray-50':
                                        highlightedIndex ===
                                        getGlobalIndex('sellers', item),
                                }"
                                @mousedown.prevent
                            >
                                <div
                                    class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded bg-[#2A3F5A] text-white overflow-hidden shadow-sm"
                                >
                                    <img
                                        v-if="item.logo_url"
                                        :src="item.logo_url"
                                        :alt="item.shop_title"
                                        loading="lazy"
                                        class="h-full w-full object-cover"
                                    />
                                    <LogoIcon
                                        v-else
                                        class="h-4 w-4 shrink-0"
                                        style="filter: brightness(0) invert(1)"
                                    />
                                </div>
                                <div class="flex-1 min-w-0">
                                    <p class="text-[15px] text-gray-700 font-mulish">
                                        {{ item.shop_title }}
                                    </p>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
